import * as SwitchPrimitive from "@radix-ui/react-switch";
import { useQuery } from "@tanstack/react-query";
import { Film, Sparkles } from "lucide-react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { MusicPicker } from "@/components/create/MusicPicker";
import { FormatPicker, VoicePicker } from "@/components/create/pickers";
import { Button, Field, Loading } from "@/components/kit";
import { PageHeader, StickyBar } from "@/components/layout";
import { useToast } from "@/components/toast";
import { api, uploadFile } from "@/lib/api";
import { useAudioPreview } from "@/lib/hooks";
import { cn } from "@/lib/utils";

function Section({ title, children, className }) {
  return (
    <div className={cn("flex flex-col gap-3", className)}>
      <h2 className="text-sm font-medium text-foreground">{title}</h2>
      {children}
    </div>
  );
}

function Summary({ items }) {
  return (
    <dl className="hidden flex-col gap-3 rounded-[20px] border border-border bg-card p-5 lg:flex" data-testid="create-video-summary">
      <p className="eyebrow">Your video</p>
      {items.map(([k, v]) => (
        <div key={k} className="flex items-baseline justify-between gap-4 border-b border-border pb-3 last:border-0 last:pb-0">
          <dt className="text-[13px] text-muted-foreground">{k}</dt>
          <dd className="truncate text-sm font-medium">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

export default function CreateVideoPage() {
  const { id } = useParams();
  const toast = useToast();
  const navigate = useNavigate();
  const project = useQuery({ queryKey: ["project", id], queryFn: () => api(`/projects/${id}`) });
  const branding = useQuery({ queryKey: ["branding"], queryFn: () => api("/branding") });
  const voices = useQuery({ queryKey: ["voices"], queryFn: () => api("/voices"), staleTime: 600_000 });
  const music = useQuery({ queryKey: ["music"], queryFn: () => api("/music"), staleTime: 600_000 });

  const [format, setFormat] = useState("9:16");
  const [script, setScript] = useState("");
  const [writing, setWriting] = useState(false);
  const [voiceover, setVoiceover] = useState(true);
  const [voiceId, setVoiceId] = useState(null);
  const [mood, setMood] = useState("upbeat");
  const [source, setSource] = useState("builtin");
  const [trackId, setTrackId] = useState(null);
  const [custom, setCustom] = useState(null);
  const [customProgress, setCustomProgress] = useState(null);
  const [creating, setCreating] = useState(false);
  const preview = useAudioPreview();

  const selVoice = voiceId ?? voices.data?.default_voice_id ?? null;
  const tracks = (music.data?.tracks ?? []).filter((t) => t.mood === mood);
  const selTrack = trackId && tracks.some((t) => t.track_id === trackId) ? trackId : tracks[0]?.track_id;
  const noMedia = (project.data?.media.length ?? 0) === 0;
  const needScript = voiceover && !script.trim();
  const needBranding = branding.data && !branding.data.complete;
  const blocked = !project.data || noMedia || needScript || !!needBranding || (source === "upload" && !custom);

  const writeScript = async () => {
    setWriting(true);
    try {
      const r = await api(`/projects/${id}/script`, { method: "POST", body: { tone: mood } });
      setScript(r.script);
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setWriting(false);
    }
  };

  const pickTrack = async (file) => {
    setCustomProgress(0);
    try {
      const m = await uploadFile("/music/upload", file, setCustomProgress);
      setCustom({ id: m.id, name: file.name });
      setSource("upload");
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setCustomProgress(null);
    }
  };

  const create = async () => {
    setCreating(true);
    preview.stop();
    try {
      const job = await api(`/projects/${id}/renders`, {
        method: "POST",
        body: {
          format, script: voiceover ? script.trim() : null, voiceover, voice_id: selVoice,
          music: source === "upload" ? { source, mood, media_id: custom?.id } : { source, mood, track_id: selTrack },
        },
      });
      navigate(`/renders/${job.id}`, { replace: true });
    } catch (e) {
      toast(e.message, "error");
      setCreating(false);
    }
  };

  const hint = noMedia && project.data ? "Add at least one photo or video" : needBranding ? "Finish your branding first (name and phone)"
    : needScript ? "Write a script or turn the voiceover off" : source === "upload" && !custom ? "Upload your music track" : null;
  const words = script.trim() ? script.trim().split(/\s+/).length : 0;

  if (!project.data && !project.isError) return <section data-testid="create-video-screen"><PageHeader title="Create video" /><Loading /></section>;

  return (
    <section data-testid="create-video-screen">
      <PageHeader title="Create video" eyebrow={project.data?.address} back={`/projects/${id}`} />
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_380px] lg:items-start">
        <div className="flex min-w-0 flex-col gap-6">
          <Section title="Format"><FormatPicker value={format} onChange={setFormat} /></Section>
          <div className="flex items-center gap-3 rounded-xl bg-card p-4">
            <div className="flex-1">
              <label htmlFor="voiceover-toggle" className="text-sm font-medium">Voiceover</label>
              <p className="text-[13px] text-muted-foreground">AI narration of your script</p>
            </div>
            <SwitchPrimitive.Root id="voiceover-toggle" checked={voiceover} onCheckedChange={setVoiceover} data-testid="voiceover-toggle"
              className="focus-ring relative h-8 w-14 shrink-0 rounded-full bg-secondary transition-colors data-[state=checked]:bg-primary">
              <SwitchPrimitive.Thumb className="block h-6 w-6 translate-x-1 rounded-full bg-foreground shadow transition-transform data-[state=checked]:translate-x-7 data-[state=checked]:bg-primary-foreground" />
            </SwitchPrimitive.Root>
          </div>
          {voiceover ? (
            <>
              <Button title={script ? "Rewrite with AI" : "Write script with AI"} icon={Sparkles} variant="secondary" onClick={writeScript}
                loading={writing} data-testid="write-script-button" />
              <Field id="script" multiline label={`Script${words ? ` · ${words} words` : ""}`} value={script} onChange={(e) => setScript(e.target.value)}
                placeholder="Click “Write script with AI” or type your own voiceover." data-testid="script-input" inputClassName="min-h-[200px]" />
              <Section title="Voice">
                {voices.isLoading ? <Loading className="py-6" /> : voices.isError ? (
                  <p className="text-[13px] text-destructive" data-testid="voices-error">{voices.error.message}</p>
                ) : <VoicePicker voices={voices.data?.voices ?? []} selected={selVoice} onSelect={setVoiceId} preview={preview} />}
              </Section>
            </>
          ) : null}
        </div>
        <div className="flex flex-col gap-6 lg:sticky lg:top-10">
          <Section title="Music">
            <MusicPicker mood={mood} source={source} tracks={tracks} selTrack={selTrack} preview={preview} custom={custom} customProgress={customProgress}
              onMood={(m) => { setMood(m); setSource("builtin"); }} onUploadMode={() => setSource("upload")} onTrack={setTrackId} onPickFile={pickTrack} />
          </Section>
          <Summary items={[
            ["Media", `${project.data?.media.length ?? 0} items`],
            ["Format", format],
            ["Voice", voiceover ? (voices.data?.voices.find((v) => v.voice_id === selVoice)?.name.split(" - ")[0] ?? "Default") : "Off"],
            ["Music", source === "upload" ? custom?.name ?? "Your track" : tracks.find((t) => t.track_id === selTrack)?.title ?? "—"],
          ]} />
        </div>
      </div>
      <StickyBar>
        {hint ? (
          needBranding ? (
            <button type="button" onClick={() => navigate("/branding")} data-testid="create-video-hint"
              className="focus-ring rounded text-center text-[13px] text-primary hover:underline sm:mr-auto">{hint}</button>
          ) : <p className="text-center text-[13px] text-muted-foreground sm:mr-auto" data-testid="create-video-hint">{hint}</p>
        ) : null}
        <Button title="Create video" icon={Film} onClick={create} loading={creating} disabled={blocked} className="sm:min-w-[240px]"
          data-testid="create-video-submit-button" />
      </StickyBar>
    </section>
  );
}
